import {
  CleaningServices as CleaningServicesIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { FramerTooltip } from "@ryde/controls";
import { IProductionStateLogData } from "@ryde/shared";

import { RICH_LOGS_PAGE_SIZE } from "../../rich-logs-components";

interface IProductionLogsStatsTabsInfo {
  state?: IProductionStateLogData;
  timeInfoText?: string;
  timeInfoClassName?: string;
  duration?: string;
  hasValidTimes?: boolean;
  formattedStartedAt?: string;
  formattedProducedRideFirstDate?: string;
  formattedProducedRideEndDate?: string;
  hasProductionData?: boolean;
  currentPage?: number;
  totalPages?: number;
  filteredLogsCount?: number;
  fetchLogs?: () => void;
  handleClearProductionCacheClick?: () => void;
  producedRideCount?: number;
  joinedRideCount?: number;
}

export const ProductionLogsStatsTabsInfo: FC<IProductionLogsStatsTabsInfo> = ({
  state,
  timeInfoText,
  timeInfoClassName,
  duration,
  hasValidTimes,
  formattedStartedAt,
  formattedProducedRideFirstDate,
  formattedProducedRideEndDate,
  hasProductionData,
  currentPage,
  totalPages,
  filteredLogsCount,
  fetchLogs,
  handleClearProductionCacheClick,
  producedRideCount,
  joinedRideCount,
}) => {
  const { t } = useTranslation();

  return (
    <div className="log__stats-tab-panel">
      {hasValidTimes && (
        <div className={timeInfoClassName}>
          <span className="log__filters-footer-time-item">
            {formattedStartedAt && `Started at: ${formattedStartedAt}`}
            {formattedStartedAt && timeInfoText && " | "}
            {timeInfoText}
            {(formattedStartedAt || timeInfoText) && duration && " | "}
            {duration && (
              <span className="log__filters-footer-duration">
                Duration: {duration}
              </span>
            )}
          </span>
        </div>
      )}

      {hasProductionData && (
        <div className="log__filters-footer-production-dates">
          {(producedRideCount ||
            joinedRideCount ||
            state?.producedAndJoinedRideIds?.length) && (
            <span className="log__filters-footer-production-date">
              <span className="log__filters-footer-production-date-label">
                {producedRideCount ? `Produced ${producedRideCount} rides` : ""}
                {producedRideCount && joinedRideCount ? ", " : ""}
                {joinedRideCount ? `joined ${joinedRideCount} rides` : ""}
                {(producedRideCount || joinedRideCount) &&
                state?.producedAndJoinedRideIds?.length
                  ? ", "
                  : ""}
                {state?.producedAndJoinedRideIds?.length
                  ? `saved ${state.producedAndJoinedRideIds.length} rides`
                  : ""}
              </span>
            </span>
          )}
          {formattedProducedRideFirstDate && (
            <span className="log__filters-footer-production-date">
              <span className="log__filters-footer-production-date-label">
                from:
              </span>
              <span className="log__filters-footer-production-date-value">
                {formattedProducedRideFirstDate}
              </span>
            </span>
          )}
          {formattedProducedRideEndDate && (
            <span className="log__filters-footer-production-date">
              <span className="log__filters-footer-production-date-label">
                until:
              </span>
              <span className="log__filters-footer-production-date-value">
                {formattedProducedRideEndDate}
              </span>
            </span>
          )}
        </div>
      )}

      <div className="log__filters-footer-main-content">
        <div className="log__filters-footer-stats">
          <div className="log__filters-footer-stats-row">
            <span className="log__filters-footer-main-count">
              Displaying {Math.min(RICH_LOGS_PAGE_SIZE, filteredLogsCount || 0)}
              <span className="log__filters-footer-separator">of</span>
              <span className="log__filters-footer-total-count">
                {filteredLogsCount || 0}
              </span>
              <span className="log__filters-footer-separator">log records</span>
              {fetchLogs && (
                <FramerTooltip content="Refresh logs">
                  <button
                    type="button"
                    className="log__filters-footer-refresh-btn"
                    onClick={fetchLogs}
                  >
                    <RefreshIcon fontSize="small" />
                  </button>
                </FramerTooltip>
              )}
              {handleClearProductionCacheClick && (
                <FramerTooltip
                  content={t("rideProduction.clearApplicationCache")}
                >
                  <button
                    type="button"
                    className="log__filters-footer-refresh-btn log__filters-footer-cache-btn"
                    onClick={handleClearProductionCacheClick}
                    aria-label={t("rideProduction.clearApplicationCache")}
                  >
                    <CleaningServicesIcon fontSize="small" />
                  </button>
                </FramerTooltip>
              )}
            </span>
            {totalPages && totalPages > 1 && (
              <span className="log__filters-footer-page-info">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
